import { create } from 'zustand';
import { persist } from 'zustand/middleware';

type LocalSettingsStoreState = {
  onlyShowRelevant: boolean;
  darkMode: 'DARK' | 'LIGHT' | 'SYSTEM';
  uiScale: number;
  isLikelyPinned: boolean;
  hasDismissedPinReminder: boolean;
  overwriteTheme: string | undefined;
  composeFontStyle: string | undefined;
  autoGenerateConceptReply: boolean;

  actions: {
    toggleOnlyShowRelevant: (onlyShowRelevant?: boolean) => void;
    toggleAutoGenerateConceptReply: (
      autoGenerateConceptReply?: boolean,
    ) => void;
    setDarkMode: (mode: 'DARK' | 'LIGHT' | 'SYSTEM') => void;
    setUiScale: (scale: number) => void;
    resetOnboarding: () => void;
    setIsLikelyPinned: (next: boolean) => void;
    dismissPinReminder: VoidFunction;
    setOverwriteTheme: (theme: string) => void;
    setComposeFontStyle: (style: string) => void;
  };
};

const LOCAL_STORAGE_KEY = 'wg-local-settings';

const INITIAL_VALUES: Omit<LocalSettingsStoreState, 'actions'> = Object.freeze({
  onlyShowRelevant: true,
  darkMode: 'SYSTEM',
  uiScale: 16,
  isLikelyPinned: false,
  hasDismissedPinReminder: false,
  overwriteTheme: undefined,
  composeFontStyle: undefined,
  autoGenerateConceptReply: true,
});

const useLocalSettings = create<LocalSettingsStoreState>()(
  persist(
    (set) => ({
      ...INITIAL_VALUES,

      actions: {
        toggleOnlyShowRelevant: (onlyShowRelevant?: boolean) =>
          set((state) => ({
            ...state,
            onlyShowRelevant:
              onlyShowRelevant !== undefined
                ? onlyShowRelevant
                : !state.onlyShowRelevant,
          })),
        toggleAutoGenerateConceptReply: (autoGenerateConceptReply?: boolean) =>
          set((state) => ({
            ...state,
            autoGenerateConceptReply:
              autoGenerateConceptReply !== undefined
                ? autoGenerateConceptReply
                : !state.autoGenerateConceptReply,
          })),
        setDarkMode: (mode) => set((state) => ({ ...state, darkMode: mode })),
        setUiScale: (uiScale) => {
          if (uiScale > 16 || uiScale < 14) return;

          set((state) => ({ ...state, uiScale }));
        },

        resetOnboarding: () =>
          set((state) => ({ ...state, onboardingCompletedAt: undefined })),
        setIsLikelyPinned: (next: boolean) =>
          set((state) => ({ ...state, isLikelyPinned: next })),
        dismissPinReminder: () =>
          set((state) => ({ ...state, hasDismissedPinReminder: true })),

        setOverwriteTheme: (theme: string) =>
          set((state) => ({ ...state, overwriteTheme: theme })),

        setComposeFontStyle: (composeFontStyle: string) =>
          set((state) => ({ ...state, composeFontStyle })),
      },
    }),
    {
      name: LOCAL_STORAGE_KEY,
      version: 0,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      partialize: ({ actions, ...rest }) => rest,
    },
  ),
);

export default useLocalSettings;
